import { Suspense, useContext } from "react";

import { FaFacebookF } from "@react-icons/all-files/fa/FaFacebookF";
import { FaInstagram } from "@react-icons/all-files/fa/FaInstagram";
import { FaTwitter } from "@react-icons/all-files/fa/FaTwitter";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Outlet, Link } from "react-router-dom-v5-compat";

import {
  TitleBar,
  ErrorBox,
  LinkButton,
} from "@onsite_app/MobileOrderingMenu/Atoms";
import { Label } from "@onsite_app/MobileOrderingMenu/Atoms/Form";
import NavBarTitle, {
  NavBarContext,
} from "@onsite_app/MobileOrderingMenu/Atoms/Navbar";
import { PageNotFound } from "@onsite_app/MobileOrderingMenu/ErrorPage";
import Loading from "@onsite_app/MobileOrderingMenu/Loading";
import { LogoImage } from "@onsite_app/MobileOrderingMenu/MainPage";
import { useVenue } from "@onsite_app/MobileOrderingMenu/VenueContext";

import { SocialLink } from "../Checkout/Layout";
import { useTimeoutSearchParam } from "../Checkout/useTimeoutSearchParam";
import VenueOnlineConfigQueries from "../VenueOnlineConfig/queries";

export const VouchersNotEnabled = () => {
  const { t } = useTranslation("onsite");
  const venue = useVenue();
  const navBarContent = useContext(NavBarContext);
  return (
    <div>
      <NavBarTitle title={t("vouchersNotEnabled.title")} />
      <div className="space-y-24 pb-64 md:py-24">
        <div className="md:flex">
          <div className="md:flex-1">
            <TitleBar>
              {navBarContent.navTitle || t("vouchersNotEnabled.title")}
            </TitleBar>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-screen-sm space-y-32 text-center">
        <div className="mx-auto w-full max-w-128">
          <LogoImage />
        </div>
        <div className="text-4xl font-bold text-venue-primary">
          {venue.name}
        </div>
        <div className="flex flex-1 items-center justify-center space-x-12">
          {venue.twitterUserName && venue.twitterUserName.trim().length > 0 && (
            <SocialLink
              href={`https://x.com/${venue.twitterUserName}`}
              icon={<FaTwitter />}
            />
          )}
          <SocialLink href={venue.facebookPageUrl} icon={<FaFacebookF />} />
          {venue.instagramUserName &&
            venue.instagramUserName.trim().length > 0 && (
              <SocialLink
                href={`https://instagram.com/${venue.instagramUserName}`}
                icon={<FaInstagram />}
              />
            )}
        </div>
        <Label>{t("vouchersNotEnabled.voucherSalesNotEnabled")}</Label>
        <LinkButton primary to={"/"}>
          Back to menu
        </LinkButton>
      </div>
    </div>
  );
};
const VouchersLayout = () => {
  const { t } = useTranslation("onsite");
  const vouchersEnabled = useSuspenseQuery({
    ...VenueOnlineConfigQueries.current(),
    select: (data) => data.onlineVoucherSales,
  }).data;

  const venue = useVenue();
  const hasJustCancelledPayment = useTimeoutSearchParam("payment");
  const navBarContent = useContext(NavBarContext);
  if (!vouchersEnabled) return <VouchersNotEnabled />;
  return (
    <Suspense fallback={<Loading loadingState="LOADING" />}>
      <NavBarTitle title={t("vouchers.title")} />
      <div className="space-y-24 pb-64 md:py-24">
        <div className="md:flex">
          <Link
            to={"/"}
            className="hidden flex-1 items-center space-x-16 md:flex"
          >
            <div className="w-full max-w-[64px]">
              <LogoImage />
            </div>
            <div className="text-lg font-bold text-venue-primary">
              {venue.name}
            </div>
          </Link>
          <div className="md:flex-1">
            <TitleBar>
              <div className="md:text-4xl md:font-bold">
                {navBarContent.navTitle || t("vouchers.title")}
              </div>
            </TitleBar>
          </div>
          <div className="hidden w-full flex-1 items-center justify-end space-x-12 md:flex">
            {venue.twitterUserName &&
              venue.twitterUserName.trim().length > 0 && (
                <SocialLink
                  href={`https://x.com/${venue.twitterUserName}`}
                  icon={<FaTwitter />}
                />
              )}
            <SocialLink href={venue.facebookPageUrl} icon={<FaFacebookF />} />
            {venue.instagramUserName &&
              venue.instagramUserName.trim().length > 0 && (
                <SocialLink
                  href={`https://instagram.com/${venue.instagramUserName}`}
                  icon={<FaInstagram />}
                />
              )}
          </div>
        </div>

        <div className="space-y-32">
          {hasJustCancelledPayment && (
            <div className="mx-auto max-w-screen-sm">
              <ErrorBox>{t("cart.paymentCancelled")}</ErrorBox>
            </div>
          )}
          <div className="rounded-venue-brand border-venue-tertiary md:mx-auto md:max-w-screen-sm md:space-y-32 md:p-16 lg:max-w-screen-lg">
            <Outlet />
          </div>
        </div>
      </div>
    </Suspense>
  );
};

export default VouchersLayout;
